import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SearchComponent.scss';
import { useSelector, useDispatch } from 'react-redux';
import { canAccessPlayerProfile, componentShouldBeDisplayed, localStorageHelper, currencyUtils } from 'utils';
import { removeSearchFromStorage, searchPlayers } from 'redux/actions/backoffice/search-actions';
import { getSearchResult, getSearchStatus } from 'redux/selectors/backoffice/search-selectors';
import { AutoComplete, Input, Tag, Select } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Search } = Input;
const { Option } = Select;

export type SearchType = 'player' | 'round' | 'transaction';

interface SearchResult {
    id: number;
    externalId: string;
    currencyId: number;
    roundId?: string;
}

interface SearchComponentProps {
    customClass?: string;
}

const SearchComponent: React.FC<SearchComponentProps> = ({ customClass }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const searchResult = useSelector(getSearchResult);
    const isSearching = useSelector(getSearchStatus);
    
    const [currentSearchString, setCurrentSearchString] = useState<string>('');
    const [searchType, setSearchType] = useState<SearchType>('player');
    const [options, setOptions] = useState<any[]>([]);
    const [showNoResults, setShowNoResults] = useState<boolean>(false);
    const currentSearchStringRef = useRef<string>('');

    const formatSearchResults = useCallback((results: SearchResult[], searchType: SearchType, searchQuery: string) => {
        if (!results || results.length === 0) return [];
        
        return results.map((result) => {
            const isRound = searchType === 'round';
            const currencyCode = getCurrencyCode(result.currencyId);
            const resultType = isRound ? 'round' : 'player';
            
            return {
                value: `${result.externalId} ${resultType} ${currencyCode}`,
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>{result.externalId}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Tag color={isRound ? 'blue' : 'green'}>
                                {isRound ? 'Round' : 'Player'}
                            </Tag>
                            <Tag color="default">
                                {currencyCode}
                            </Tag>
                        </div>
                    </div>
                ),
                result: result,
                searchQuery: searchQuery
            };
        });
    }, []);

    const getCurrencyCode = (currencyId: number): string => {
        const currencyData = currencyUtils.getCurrencyDataById(currencyId);
        return currencyData?.code || `CURRENCY_${currencyId}`;
    };

    const handleSearch = useCallback((value: string) => {
        const truncatedValue = value.length > 100 ? value.substring(0, 100) : value;
        setCurrentSearchString(truncatedValue);
        if (truncatedValue && truncatedValue.length >= 3) {
            setOptions([]);
            setShowNoResults(false);
            dispatch(searchPlayers({ searchString: truncatedValue, searchType }));
        } else {
            setOptions([]);
            setShowNoResults(false);
        }
    }, [dispatch, searchType]);

    const handleSelect = useCallback((value: string, option: any) => {
        const result = option.result;
        if (result) {
            const searchQuery = option.searchQuery || currentSearchString.trim();
            setCurrentSearchString('');
            setOptions([]);
            
            if (searchType === 'round') {
                navigate(`/casino/players/${result.id}/${result.externalId}?roundId=${encodeURIComponent(searchQuery)}`);
            } else if (result.roundId) {
                navigate(`/casino/players/${result.id}/${result.externalId}?roundId=${result.roundId}`);
            } else {
                navigate(`/casino/players/${result.id}/${result.externalId}`);
            }
        }
    }, [navigate, searchType, currentSearchString]);

    const handleSearchSubmit = useCallback((value: string) => {
        if (value && value.trim().length > 0) {
            if (options.length > 0) {
                const firstOption = options[0];
                handleSelect(firstOption.value, firstOption);
                return;
            }
            setOptions([]);
            setShowNoResults(false);
            dispatch(searchPlayers({ searchString: value.trim(), searchType }));
        }
    }, [dispatch, searchType, options, handleSelect]);


    useEffect(() => {
        if (searchResult && searchResult.content) {
            const formattedOptions = formatSearchResults(searchResult.content, searchType, currentSearchString);
            setOptions(formattedOptions);
            if (formattedOptions.length === 0 && currentSearchString.length >= 3) {
                setShowNoResults(true);
                const timer = setTimeout(() => {
                    setShowNoResults(false);
                }, 3000);
                return () => clearTimeout(timer);
            } else {
                setShowNoResults(false);
            }
        } else if (searchResult && (!searchResult.content || searchResult.content.length === 0) && currentSearchString.length >= 3 && !isSearching) {
            setShowNoResults(true);
            const timer = setTimeout(() => {
                setShowNoResults(false);
            }, 3000);
            return () => clearTimeout(timer);
        } else {
            setShowNoResults(false);
        }
    }, [searchResult, formatSearchResults, searchType, currentSearchString, isSearching]);

    useEffect(() => {
        currentSearchStringRef.current = currentSearchString;
    }, [currentSearchString]);

    useEffect(() => {
        setOptions([]);
        setShowNoResults(false);
        
        const searchString = currentSearchStringRef.current;
        if (searchString && searchString.length >= 3) {
            dispatch(searchPlayers({ searchString, searchType }));
        }
    }, [searchType, dispatch]);

    useEffect(() => {
        return () => {
            const { pathname } = window.location;
            const searchString = localStorageHelper.getChainedValue('user.search.currentSearchString');
            const isStringInStorage = searchString && searchString.length > 0;
            const isNotOnResultsPage =
                pathname.split('/')[2] !== 'search-result' && pathname.split('/')[2] !== 'players';

            if (isNotOnResultsPage && isStringInStorage) {
                dispatch(removeSearchFromStorage());
            }
        };
    }, [dispatch]);

    if (!componentShouldBeDisplayed('searchField') || !canAccessPlayerProfile()) {
        return null;
    }

    const getPlaceholder = () => {
        switch (searchType) {
            case 'player':
                return 'Search player';
            case 'round':
                return 'Search round';
            case 'transaction':
                return 'Search transaction ID';
            default:
                return 'Search';
        }
    };

    return (
        <div className={customClass} style={{ display: 'flex', width: '100%' }}>
            <Select
                className="rounded-r-none w-[170px]"
                value={searchType}
                onChange={setSearchType}
            >
                <Option value="player">External player ID</Option>
                <Option value="round">Round ID</Option>
                <Option value="transaction">Transaction ID</Option>
            </Select>
            <AutoComplete
                className="rounded-l-none"
                options={currentSearchString.length >= 3 ? options : []}
                onSearch={handleSearch}
                onSelect={handleSelect}
                value={currentSearchString}
                placeholder={getPlaceholder()}
                style={{ flex: 1 }}
                notFoundContent={isSearching && currentSearchString.length >= 3 ? 'Searching...' : 'No results found'}
                filterOption={false}
                open={currentSearchString.length >= 3 && (options.length > 0 || isSearching || showNoResults)}
            >
                <Search
                    className="rounded-l-none"
                    enterButton
                    allowClear
                    loading={isSearching && currentSearchString.length >= 3}
                    onSearch={handleSearchSubmit}
                    onChange={(e) => handleSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && options.length > 0) {
                            e.preventDefault();
                            const firstOption = options[0];
                            handleSelect(firstOption.value, firstOption);
                        }
                    }}
                />
            </AutoComplete>
        </div>
    );
};

export default SearchComponent;
